import React, { useState } from 'react';
import withContext from '../common/withContext';
import ProjectContext from './projectContext';
import { SdfAlert, SdfLink, SdfBox, SdfPromoBox, SdfButton, SdfIcon } from '@synerg/react-components';
import get from 'lodash/get';
import ProjectCard from './projectCard';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import GoLiveModal from './goLiveModal';
import { ManageDevelopmentProject } from './manageDevelopmentProject';

const PARTNER_SUPPORT = 'https://partnersupport.adp.com';

function ManageProject({ context, associatedProject, onNavigation, setHasPageNavigated, isDataCloudProject, setSelectedTab, projectDetail }) {
	const history = useHistory();

	const { legacy, type, link, status, createdDate = '', platform, complete = false, _id: projectId, appListingUrls, milestones } = context.projectDetail;
	const isDevAssociated = type === 'DEV' && link;
	const isSuspended = (status === 'SUSPENDED');
	const tabToSet = type === 'PROD' ? 'Production Credentials' : 'Development Credentials';
	const isProdAssociated = type === 'PROD' && link;
	const nonLegacyProd = type === 'PROD' && !legacy;
	const isMarketPlacePartner = platform === 'marketplace';
	const promoBoxText = 'When the remaining milestones are completed, notify your Marketplace Technical Advisor (MTA) you are ready to publish your app.';
	const canDisplayNonLegacy = status === 'LIVE';
	const [isProdProjectCompleted, setIsProdProjectCompleted] = useState(complete);
	const [showGoLiveModal, setShowGoLiveModal] = useState(false);
	const isUnderReview = status === 'NOT_LIVE' && isProdProjectCompleted;
	const canDisplayUrls = type === 'PROD' && Array.isArray(appListingUrls) && appListingUrls.length > 0;
	const nonLegacyDev = type === 'DEV' && !legacy;
	const isDevFinishedBefore = Array.isArray(milestones) && milestones.length === 0 && complete;

	const goToAssociatedProject = () => {
		onNavigation();
		setHasPageNavigated(true);
		history.push(`/projects/${link}`);
	};
	const closeGoLiveModal = () => setShowGoLiveModal(false);

	return (
		<div>

			<h1 className='text-heading-07 font-black'> Manage {type === 'PROD' ? 'production' : 'development'} project</h1>

			{isSuspended && <SdfAlert className='w-4/6 mb-2' type='banner' status='error' size='sm' icon='alert-error'>
				<h1 className='text-heading-03' slot='title'>This project is suspended </h1>
				<p className='my-0'>For any questions, please contact <SdfLink emphasis="none" href={PARTNER_SUPPORT} target="_blank">partnersupport.adp.com</SdfLink> </p>
			</SdfAlert>}

			{nonLegacyProd && isUnderReview && isMarketPlacePartner &&
				<SdfAlert className='w-4/6 mb-2' type='banner' status='warning' size='sm' icon='status-flagged'>
					<h1 className='text-heading-03 font-bold' style={{ letterSpacing: '-1px' }} slot='title'>Under review for publishing</h1>
					<p className='my-0 font-normal' style={{ lineHeight: '1.71' }} >You will be notified by email when the project has been published.</p>
				</SdfAlert>
			}

			{nonLegacyProd && canDisplayNonLegacy && isMarketPlacePartner &&
				<SdfAlert className='w-4/6 mb-2' type='banner' status='success' size='sm' icon='alert-success'>
					<h1 className='text-heading-03' slot='title'>Congratulations</h1>
					<p className='my-0'>Your production project is published on ADP Marketplace. Please review the listing(s) below.</p>
				</SdfAlert>
			}

			{canDisplayUrls && !isSuspended && (canDisplayNonLegacy || legacy) && isMarketPlacePartner &&
				<SdfBox className='w-4/6 mb-2' variant='bordered' size='sm'>
					<h6 className='text-heading-03 my-0'>Marketplace listing(s)</h6>
					<div className='flex flex-col my-2'>
						{Array.isArray(appListingUrls) && appListingUrls.map(({ url, name }) => (
							<SdfLink className='text-sm mt-2' emphasis="none" href={url} target="_blank"> <SdfIcon icon="external-link" />  <span> View {name} </span> </SdfLink>
						))}
					</div>
				</SdfBox>
			}

			{isProdAssociated &&
				<SdfAlert className='w-4/6 mb-10' type='banner' status='info' size='sm' icon='alert-notification' spacing="tight">
					<div className='flex items-baseline justify-between'>
						<div className='leading-sm text-md'>Production project {nonLegacyProd ? 'created' : 'imported'} on {moment(createdDate).format('MMM DD, YYYY')}. Looking for an associated development project?</div>
						{link && <SdfLink className='cursor-pointer text-md' onClick={goToAssociatedProject}>Find it here</SdfLink>}
						{!link && <div>&nbsp;</div>}
					</div>
				</SdfAlert>
			}

			{nonLegacyProd && !isSuspended &&
				<div>
					<h3 className='text-heading-06 font-bold'>Production milestones</h3>
					<p className='text-body-02 w-5/6 inline'>
						{isMarketPlacePartner ?
							'Below you’ll find additional milestones that must be completed before publishing a project into an app.' :
							'Below you’ll find additional milestones that must be completed.'}
						{!isDataCloudProject && <> For more detailed instructions, refer to the&nbsp;
							<SdfLink href='https://developers.adp.com/articles/guides/production-checklist/' target='_blank' rel='noopener noreferrer'> production project guide&nbsp;</SdfLink>
							on ADP Developer Resources.</>}
					</p>

					<div className='flex mt-12 gap-10'>
						<div className='flex flex-col gap-4 w-4/6'>
							<SdfBox className='font-bold text-lg' variant='bordered'>Configure integration with production  <SdfLink className='cursor-pointer' onClick={() => setSelectedTab(tabToSet)}>credentials</SdfLink></SdfBox>
							<SdfBox className='font-bold text-lg' variant='bordered'>{isMarketPlacePartner ? 'Live subscription testing' : 'Live testing'} </SdfBox>
						</div>
						{!isProdProjectCompleted && !canDisplayNonLegacy && isMarketPlacePartner && <SdfPromoBox className="w-sm" variant='bordered' size='lg' spacing='loose' align='center' illustrationName='cloud-loading' header='Ready to publish?' message={promoBoxText}>
							<SdfButton slot='cta' emphasis='primary' className='mt-6' onClick={() => setShowGoLiveModal(true)}> Request to publish</SdfButton>
						</SdfPromoBox>}
					</div>
				</div>
			}


			{legacy && !isSuspended &&
				<SdfAlert className='w-4/6' type='banner' status='neutral' hideIcon='true' size='sm' closeable='true'>
					<h3 slot='title'>Legacy Project</h3>
					<p className='text-body-01'>
						This is a project that previously existed in our system but was not created
						with this self-service tool using the manage project feature.
						However, you can manage your {type === 'PROD' ? 'Production' : 'Development'} API credentials.
					</p>
					<SdfLink onClick={() => setSelectedTab(tabToSet)}>Manage {type === 'PROD' ? 'production' : 'development'} credentials</SdfLink>
				</SdfAlert>
			}


			{nonLegacyDev && !isDevFinishedBefore && <ManageDevelopmentProject projectDetail={projectDetail} isDataCloudUser={isDataCloudProject} />}

			{isDevAssociated &&
				<div>
					<h1 className='text-heading-06'>Access Production Project</h1>
					<p className='text-body-02'>The production project has been generated. You may access production API credentials and perform live subscription testing there. Select the project card below to access the production project.</p>

					<ProjectCard
						project={associatedProject}
						product={get(context.projectDetail, 'product', '')}
						type='PROD'
						goToAssociatedProject={goToAssociatedProject}
					/>

				</div>
			}

			<GoLiveModal projectId={projectId} showGoLiveModal={showGoLiveModal} closeGoLiveModal={closeGoLiveModal} setIsProdProjectCompleted={setIsProdProjectCompleted} reLoadPage={setHasPageNavigated} />
		</div>
	);
}

export default withContext(ManageProject, ProjectContext);
